import React from "react";
import { Box, Text, Tooltip } from "@chakra-ui/react";

/**
 * Renders a single detail row with an icon, label, and value.
 * Used to display service information in a compact, styled row.
 * @param {Object} props
 * @param {React.ReactElement} props.icon - Icon element to display (react-icons)
 * @param {string} props.label - Label text for the row
 * @param {string|React.ReactNode} props.value - Value content to display
 * @param {string} [props.tooltip] - Optional tooltip text on the icon
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function DetailRow({ icon, label, value, tooltip, isMobile }) {
    return (
        <Box {...rowContainerProps(isMobile)}>
            {icon && (
                <Tooltip label={tooltip || label} hasArrow placement="top">
                    {React.cloneElement(icon, {
                        boxSize: isMobile ? "16px" : "18px", flexShrink: 0
                    })}
                </Tooltip>
            )}
            <Text {...labelProps(isMobile)}>
                {label}
            </Text>
            <Text {...valueProps(isMobile)}>
                {value || "Non renseigne"}
            </Text>
        </Box>
    );
}

/**
 * Returns the style props for the row container box.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props
 */
function rowContainerProps(isMobile) {
    return {
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: isMobile ? "6px 10px" : "7px 12px",
        marginBottom: "8px",
        background: "rgba(255, 255, 255, 0.04)",
        backdropFilter: "blur(10px)",
        borderRadius: "8px",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        transition: "all 0.3s ease",
        _hover: {
            background: "rgba(255, 255, 255, 0.06)",
            border: "1px solid rgba(255, 255, 255, 0.12)"
        }
    };
}

/**
 * Returns the style props for the label text.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props
 */
function labelProps(isMobile) {
    return {
        color: "rgba(255, 255, 255, 0.6)",
        fontSize: isMobile ? "0.65rem" : "0.7rem",
        fontWeight: 600,
        minWidth: isMobile ? "70px" : "80px",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        flexShrink: 0
    };
}

/**
 * Returns the style props for the value text.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props
 */
function valueProps(isMobile) {
    return {
        color: "rgba(255, 255, 255, 0.85)",
        fontSize: isMobile ? "0.75rem" : "0.8rem",
        fontWeight: 500,
        flex: 1,
        textAlign: "right",
        wordBreak: "break-word"
    };
}

export default DetailRow;
